import * as React from 'react'
import { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { listAllUsers } from '@/features/admin/api'
import { ROLE_LABELS, type Role } from '@/features/auth/types'
import { InviteUserDrawer } from './InviteUserDrawer'
import { AdminSidebar } from '@/components/layout/AdminSidebar'
import { Button } from '@/components/primitives/Button'
import { Pagination } from '@/components/primitives/Pagination'
import { PageLoader } from '@/components/feedback/PageLoader'
import '../ManagePages.css'

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—'
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function userInitials(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

const AVATAR_COLORS = ['#4f6ef7', '#0ea5a0', '#7c3aed', '#d97706', '#db2777', '#059669', '#0284c7']
function avatarColor(name: string): string {
  const idx = (name.charCodeAt(0) + (name.charCodeAt(1) ?? 0)) % AVATAR_COLORS.length
  return AVATAR_COLORS[idx] ?? '#4f6ef7'
}

const ROLE_BADGE_MOD: Record<string, string> = {
  THINKADMIN: 'thinkadmin',
  SUPER_ADMIN: 'admin',
  NURSE: 'nurse',
  VIEWER: 'viewer',
}

export function AdminUsersPage(): React.JSX.Element {
  const [inviteOpen, setInviteOpen] = useState(false)
  const [search, setSearch]         = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage]             = useState(1)
  const [perPage, setPerPage]       = useState(25)

  const usersQuery = useQuery({ queryKey: ['admin-users'], queryFn: listAllUsers })
  const allUsers = usersQuery.data ?? []

  const stats = useMemo(() => ({
    total: allUsers.length,
    admins: allUsers.filter((u) => u.role === 'SUPER_ADMIN').length,
    nurses: allUsers.filter((u) => u.role === 'NURSE').length,
    pending: allUsers.filter((u) => u.status === 'invited').length,
  }), [allUsers])

  const users = useMemo(() => {
    let r = [...allUsers]
    if (roleFilter) r = r.filter(u => u.role === roleFilter)
    if (statusFilter) r = r.filter(u => u.status === statusFilter)
    if (search.trim()) {
      const q = search.toLowerCase()
      r = r.filter(u =>
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.tenant_name.toLowerCase().includes(q),
      )
    }
    r.sort((a, b) => {
      const da = a.created_at ? new Date(a.created_at).getTime() : 0
      const db = b.created_at ? new Date(b.created_at).getTime() : 0
      return db - da
    })
    return r
  }, [allUsers, search, roleFilter, statusFilter])

  useEffect(() => { setPage(1) }, [search, roleFilter, statusFilter])

  const totalPages = Math.max(1, Math.ceil(users.length / perPage))
  const pageUsers  = users.slice((page - 1) * perPage, page * perPage)

  return (
    <div className="admin-shell">
      <AdminSidebar />
      <div className="admin-shell-content">
      <InviteUserDrawer open={inviteOpen} onClose={() => setInviteOpen(false)} />
      <main className="manage-main">
        <div className="manage-head-row">
          <div>
            <h1 className="manage-title">Users</h1>
            <p className="manage-sub">All users across every organization.</p>
          </div>
          <Button onClick={() => setInviteOpen(true)} leftIcon={<PlusIcon />}>Invite user</Button>
        </div>

        {/* ── Stats ── */}
        {users.length > 0 && (
          <div className="admin-stats">
            <div className="admin-stat">
              <div className="admin-stat-value">{stats.total}</div>
              <div className="admin-stat-label">Total users</div>
            </div>
            <div className="admin-stat">
              <div className="admin-stat-value">{stats.admins}</div>
              <div className="admin-stat-label">Admins</div>
            </div>
            <div className="admin-stat">
              <div className="admin-stat-value">{stats.nurses}</div>
              <div className="admin-stat-label">Nurses</div>
            </div>
            <div className="admin-stat">
              <div className="admin-stat-value admin-stat-value--warn">{stats.pending}</div>
              <div className="admin-stat-label">Pending invite</div>
            </div>
          </div>
        )}

        <div className="manage-card-table">
          <div className="manage-card-table-header">
            <div>
              <div className="manage-card-table-title">All users</div>
              {allUsers.length > 0 && (
                <div className="manage-card-table-sub">{users.length} of {allUsers.length} user{allUsers.length !== 1 ? 's' : ''}</div>
              )}
            </div>
            <div className="manage-toolbar">
              <input
                className="manage-search"
                placeholder="Search by name or email…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <select className="select manage-filter-select" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                <option value="">All roles</option>
                <option value="SUPER_ADMIN">Admin</option>
                <option value="NURSE">Nurse</option>
                <option value="VIEWER">Viewer</option>
              </select>
              <select className="select manage-filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="invited">Invited</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
          </div>

          {usersQuery.isLoading ? (
            <div style={{ padding: 'var(--space-8)' }}><PageLoader /></div>
          ) : allUsers.length === 0 ? (
            <div className="manage-empty-state">
              <UserIcon />
              <p>No users yet.</p>
            </div>
          ) : users.length === 0 ? (
            <div className="manage-empty-state"><p>No results match your filters.</p></div>
          ) : (
            <table className="manage-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Organization · Facility</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {pageUsers.map((u) => {
                  const color = avatarColor(u.name)
                  const roleMod = ROLE_BADGE_MOD[u.role] ?? 'viewer'
                  return (
                    <tr key={u.id}>
                      <td>
                        <div className="admin-cell-with-avatar">
                          <div className="admin-avatar" style={{ background: color }}>
                            {userInitials(u.name)}
                          </div>
                          <div>
                            <div className="manage-name">{u.name}</div>
                            <div className="manage-email">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="manage-name" style={{ fontWeight: 400 }}>{u.tenant_name}</div>
                        {u.facility_name && (
                          <div className="manage-email">{u.facility_name}</div>
                        )}
                      </td>
                      <td>
                        <span className={`manage-role-badge manage-role-badge--${roleMod}`}>
                          {ROLE_LABELS[u.role as Role] ?? u.role}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge status-badge--${u.status}`}>
                          {u.status}
                        </span>
                      </td>
                      <td><span className="manage-email">{fmtDate(u.created_at)}</span></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
          <Pagination page={page} totalPages={totalPages} total={users.length} perPage={perPage} onPage={setPage} onPerPage={n => { setPerPage(n); setPage(1) }} />
        </div>
      </main>
      </div>
    </div>
  )
}

function UserIcon(): React.JSX.Element {
  return <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
}
function PlusIcon(): React.JSX.Element {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
}
